if (!customElements.get('steves-store-pickup')) {
  customElements.define(
    'steves-store-pickup',
    class StevesStorePickup extends HTMLElement {
      constructor() {
        super();

        this.abortController = null;
        this.variantChangeUnsubscriber = null;
        this.defaultStores = [
          { store: 'Toronto', availability: 0 },
          { store: 'Ottawa', availability: 0 },
          { store: 'Greenfield Park', availability: 0 },
          { store: 'Dollard-Des-Ormeaux', availability: 0 },
          { store: 'Montreal', availability: 0 },
          { store: 'Online', availability: 0 },
        ];
        this.storeDetails = {
          montreal: { name: 'Montreal', phone: '514-878-2216' },
        };
        this.fallbackStrings = {
          inStock: 'In stock',
          outOfStock: 'Out of stock',
          outOfStockMessage:
            'This item is not currently in stock but is available for purchase. You can contact our Internet sales department at support@stevesmusic.com for inquiries about expected delivery dates.',
          availableOnline: 'Available to purchase online',
          callForAvailability: 'Call for availability',
          montrealStoreName: 'Montreal',
          montrealPickupOption: 'Montreal, 150 Ste-Catherine St. E',
          pickupButton: 'In-store pickup',
          fullName: 'Full name',
          store: 'Store',
          select: 'Select',
          email: 'Email',
          phone: 'Phone',
          order: 'Order',
          nameRequired: 'Full name is required',
          pickupSuccess: 'Thank you! Your order was submitted successfully',
          genericError: 'Something went wrong. Please try again.',
          validationError: 'Please check the highlighted fields.',
          notifyMeButton: 'Notify me when available',
          notifyMeSubmit: 'Notify me',
          cancel: 'Cancel',
          preferredLocation: 'Your preferred location',
          postalCode: 'Postal Code',
          optional: 'optional',
          keepMePosted: 'Keep me updated by email and SMS.',
          emailUpdates: 'Email',
          smsUpdates: 'SMS',
          smsAgreement: "By signing via text, you agree to receive recurring automated promotional and personalized marketing text messages from Steve's Music. You can unsubscribe anytime.",
          reminderSuccess: 'Thank you. We will let you know when this item is available.',
          floorUnitMessageHtml:
            '<strong>This item is a Floor/Display unit and discounted accordingly. If you would like more information about this item please call:</strong>',
        };
      }

      connectedCallback() {
        this.hydrate();
        this.subscribeToVariantChanges();
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.variantChangeUnsubscriber?.();
      }

      hydrate() {
        this.abortController?.abort();
        this.abortController = new AbortController();

        this.cacheElements();
        this.strings = { ...this.fallbackStrings, ...this.readJson('[data-strings]', {}) };
        this.productData = this.readJson('[data-product-json]', {});
        this.inventory = this.getInventory();

        this.bindEvents();
        this.normalizeInitialFields();
        this.renderAvailability();
        this.validatePickupForm();
      }

      cacheElements() {
        this.availabilityWrapper = this.querySelector('[data-availability-wrapper]');
        this.availabilityList = this.querySelector('[data-availability-list]');
        this.inStockTitle = this.querySelector('[data-in-stock-title]');
        this.outOfStockPanel = this.querySelector('[data-out-of-stock]');
        this.floorUnitMessage = this.querySelector('[data-floor-unit-message]');

        this.pickupParent = this.querySelector('[data-pickup-parent-container]');
        this.pickupToggle = this.querySelector('[data-pickup-toggle]');
        this.pickupForm = this.querySelector('[data-pickup-form]');
        this.pickupFullName = this.querySelector('[data-pickup-full-name]');
        this.pickupStore = this.querySelector('[data-pickup-store]');
        this.pickupEmail = this.querySelector('[data-pickup-email]');
        this.pickupPhone = this.querySelector('[data-pickup-phone]');
        this.pickupSubmit = this.querySelector('[data-pickup-submit]');
        this.pickupMessage = this.querySelector('[data-pickup-message]');
        this.statusMessage = this.querySelector('[data-status-message]');

        this.reminderTrigger = this.querySelector('[data-reminder-trigger]');
        this.reminderModal = this.querySelector('basic-modal');
        this.reminderLocation = this.querySelector('[data-reminder-location]');
        this.reminderEmail = this.querySelector('[data-reminder-email]');
        this.reminderPhone = this.querySelector('[data-reminder-phone]');
        this.reminderPostalCode = this.querySelector('[data-reminder-postal-code]');
        this.reminderEmailConsent = this.querySelector('[data-reminder-email-consent]');
        this.reminderSmsConsent = this.querySelector('[data-reminder-sms-consent]');
        this.reminderSubmit = this.querySelector('[data-reminder-submit]');
        this.reminderMessage = this.querySelector('[data-reminder-message]');
      }

      bindEvents() {
        const signal = this.abortController.signal;

        this.pickupToggle?.addEventListener('click', this.togglePickupForm.bind(this), { signal });
        this.pickupStore?.addEventListener('change', this.validatePickupForm.bind(this), { signal });
        this.pickupEmail?.addEventListener('input', this.handlePickupEmailInput.bind(this), { signal });
        this.pickupPhone?.addEventListener('input', this.handlePickupPhoneInput.bind(this), { signal });
        this.pickupSubmit?.addEventListener('click', this.submitPickupRequest.bind(this), { signal });

        this.reminderEmail?.addEventListener('input', () => this.clearFieldError(this.reminderEmail), { signal });
        this.reminderPhone?.addEventListener('input', this.handleReminderPhoneInput.bind(this), { signal });
        this.reminderPostalCode?.addEventListener('input', this.handlePostalCodeInput.bind(this), { signal });
        this.reminderSubmit?.addEventListener('click', this.submitAvailabilityReminder.bind(this), { signal });
      }

      normalizeInitialFields() {
        if (this.pickupPhone?.value.trim()) {
          this.handlePickupPhoneInput();
        }

        if (this.pickupEmail?.value.trim()) {
          this.handlePickupEmailInput();
        }

        if (this.reminderPhone?.value.trim()) {
          this.handleReminderPhoneInput();
        }

        if (this.reminderPostalCode?.value.trim()) {
          this.handlePostalCodeInput();
        }
      }

      subscribeToVariantChanges() {
        if (this.variantChangeUnsubscriber || !window.FoxTheme?.pubsub) return;

        this.variantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
          this.handleVariantChange.bind(this)
        );
      }

      handleVariantChange(event) {
        const data = event?.data;
        if (!data || data.sectionId !== this.dataset.sectionId || !data.html) return;

        const next = data.html.querySelector(`steves-store-pickup[data-section-id="${this.dataset.sectionId}"]`);
        if (!next) {
          this.hidden = true;
          return;
        }

        this.closeReminderModal();
        this.replaceChildren(...Array.from(next.childNodes).map((child) => child.cloneNode(true)));
        Object.keys(next.dataset).forEach((key) => {
          this.dataset[key] = next.dataset[key];
        });
        this.hidden = false;
        this.hydrate();
      }

      readJson(selector, fallback) {
        const script = this.querySelector(selector);
        if (!script) return fallback;

        const raw = script.textContent.trim();
        if (!raw) return fallback;

        try {
          const parsed = JSON.parse(raw);
          return typeof parsed === 'string' ? JSON.parse(parsed) : parsed;
        } catch (error) {
          console.warn('Unable to parse Steve store pickup JSON', error);
          return fallback;
        }
      }

      getInventory() {
        const parsed = this.readJson('[data-inventory-json]', this.defaultStores);
        return Array.isArray(parsed) ? parsed : this.defaultStores;
      }

      renderAvailability() {
        if (!this.availabilityWrapper || !this.availabilityList) return;

        const visibleStores = this.getVisibleStores();
        const isOutOfStock = !visibleStores.some((store) => store.rawAvailability > 0);

        this.availabilityWrapper.hidden = false;
        this.availabilityList.innerHTML = '';
        this.inStockTitle.hidden = isOutOfStock;
        this.outOfStockPanel.hidden = !isOutOfStock;
        this.pickupParent.hidden = isOutOfStock;
        this.reminderTrigger.hidden = !isOutOfStock;

        if (isOutOfStock) {
          this.floorUnitMessage.hidden = true;
          this.closePickupForm();
          return;
        }

        visibleStores.forEach((store) => {
          this.availabilityList.appendChild(this.createStoreAvailability(store));
        });

        this.renderFloorUnitMessage();
      }

      getVisibleStores() {
        const hasPhysicalAvailability = this.inventory.some((item) => {
          return this.normalizeStore(item.store) !== 'online' && Number(item.availability) > 0;
        });

        return this.inventory
          .filter((item) => {
            const store = this.normalizeStore(item.store);
            return store === 'montreal' || store === 'online';
          })
          .map((item) => {
            const store = this.normalizeStore(item.store);
            const rawAvailability = Number(item.availability) || 0;
            let displayAvailability = rawAvailability;

            if (store === 'online' && hasPhysicalAvailability) {
              displayAvailability = 1;
            }

            return {
              store,
              title: this.getDisplayStoreName(store, item.store),
              rawAvailability,
              displayAvailability,
            };
          });
      }

      getDisplayStoreName(store, fallback) {
        if (store === 'online') return this.strings.availableOnline;
        if (store === 'montreal') return this.strings.montrealStoreName;
        return fallback;
      }

      createStoreAvailability(store) {
        const item = document.createElement('div');
        item.className = 'steves-store-pickup__store';

        const isAvailable = store.displayAvailability > 0;
        const icon = document.createElement('span');
        icon.className = `steves-store-pickup__store-icon${isAvailable ? '' : ' steves-store-pickup__store-icon--unavailable'}`;
        icon.appendChild(this.cloneIcon(isAvailable ? '[data-icon-available]' : '[data-icon-unavailable]'));

        const content = document.createElement('div');
        const title = document.createElement('p');
        title.className = 'steves-store-pickup__store-name font-body-bolder';
        title.textContent = store.title;
        content.appendChild(title);

        if (!isAvailable && this.storeDetails[store.store]) {
          const help = document.createElement('p');
          help.className = 'steves-store-pickup__store-help text-subtext';
          help.textContent = `${this.strings.callForAvailability} ${this.storeDetails[store.store].phone}`;
          content.appendChild(help);
        }

        item.append(icon, content);
        return item;
      }

      cloneIcon(selector) {
        const template = this.querySelector(selector);
        const icon = template?.content?.firstElementChild;

        if (icon) return icon.cloneNode(true);

        const fallback = document.createElement('span');
        fallback.textContent = selector.includes('unavailable') ? '-' : '+';
        return fallback;
      }

      renderFloorUnitMessage() {
        if (!this.floorUnitMessage) return;

        const isFloorUnitProduct = this.dataset.floorUnit === 'true';
        const physicalAvailability = this.inventory
          .filter((item) => this.normalizeStore(item.store) !== 'online')
          .map((item) => Number(item.availability) || 0);

        const hasOneItem = physicalAvailability.some((availability) => availability === 1);
        const hasGreaterThanOneItem = physicalAvailability.some((availability) => availability > 1);

        this.floorUnitMessage.hidden = !(isFloorUnitProduct && hasOneItem && !hasGreaterThanOneItem);
      }

      normalizeStore(store) {
        return String(store || '').trim().toLowerCase();
      }

      togglePickupForm() {
        const willOpen = this.pickupForm.hidden;
        this.pickupForm.hidden = !willOpen;
        this.pickupToggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
        this.hideMessage(this.pickupMessage);
        this.validatePickupForm();
      }

      closePickupForm() {
        if (!this.pickupForm) return;
        this.pickupForm.hidden = true;
        this.pickupToggle?.setAttribute('aria-expanded', 'false');
      }

      closeReminderModal() {
        if (this.reminderModal?.open) {
          this.reminderModal.hide();
        }
      }

      handlePickupPhoneInput() {
        const result = this.formatPhone(this.pickupPhone.value);
        this.pickupPhone.value = result.value;
        this.setFieldError(this.pickupPhone, !result.valid);
        this.validatePickupForm();
      }

      handleReminderPhoneInput() {
        if (!this.reminderPhone.value.trim()) {
          this.clearFieldError(this.reminderPhone);
          return;
        }

        const result = this.formatPhone(this.reminderPhone.value);
        this.reminderPhone.value = result.value;
        this.setFieldError(this.reminderPhone, !result.valid);
      }

      handlePickupEmailInput() {
        this.setFieldError(this.pickupEmail, !this.isValidEmail(this.pickupEmail.value));
        this.validatePickupForm();
      }

      handlePostalCodeInput() {
        const value = this.reminderPostalCode.value
          .replace(/[^a-z0-9]/gi, '')
          .slice(0, 6)
          .toUpperCase();

        this.reminderPostalCode.value = value.length > 3 ? `${value.slice(0, 3)} ${value.slice(3)}` : value;
        this.clearFieldError(this.reminderPostalCode);
      }

      validatePickupForm() {
        if (!this.pickupSubmit) return;

        const hasStore = this.pickupStore.value.trim() !== '';
        const hasValidPhone = this.pickupPhone.value.trim() !== '' && !this.pickupPhone.classList.contains('border-danger');
        const hasValidEmail = this.pickupEmail.value.trim() !== '' && !this.pickupEmail.classList.contains('border-danger');

        this.pickupSubmit.disabled = !(hasStore && hasValidPhone && hasValidEmail);
      }

      formatPhone(value) {
        const numbers = String(value || '').replace(/\D/g, '').slice(0, 11);

        if (numbers.length === 10) {
          return {
            valid: true,
            value: numbers.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3'),
          };
        }

        if (numbers.length === 11) {
          return {
            valid: true,
            value: numbers.replace(/(\d{1})(\d{3})(\d{3})(\d{4})/, '+$1 ($2) $3-$4'),
          };
        }

        return { valid: false, value: numbers };
      }

      isValidEmail(value) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
      }

      setFieldError(field, hasError) {
        if (!field) return;

        field.classList.toggle('border-danger', hasError);
        field.setAttribute('aria-invalid', hasError ? 'true' : 'false');
      }

      clearFieldError(field) {
        this.setFieldError(field, false);
      }

      async submitPickupRequest() {
        this.hideMessage(this.pickupMessage);
        this.hideMessage(this.statusMessage);
        this.validatePickupForm();

        if (this.pickupSubmit.disabled) return;

        let firstName = this.dataset.customerFirstName.trim();
        let lastName = this.dataset.customerLastName.trim();

        if (!firstName || !lastName) {
          const fullName = this.pickupFullName.value.trim();
          if (!fullName) {
            this.showMessage(this.pickupMessage, this.strings.nameRequired, 'error');
            return;
          }

          const nameParts = fullName.split(/\s+/);
          firstName = nameParts.shift() || '';
          lastName = nameParts.join(' ');
        }

        const requestData = {
          action: 'create-pickup',
          customer_id: this.dataset.customerId,
          first_name: firstName,
          last_name: lastName,
          email: this.pickupEmail.value.trim(),
          store: this.pickupStore.value.trim(),
          phone: this.pickupPhone.value.trim(),
          qty: this.getQuantity(),
          product: this.productData,
          title: this.dataset.productTitle || this.productData.title || '',
          language: this.dataset.language,
        };

        this.setLoading(this.pickupSubmit, true);

        try {
          const response = await this.postForm('https://app.stevesmusic.com/api/store-pickup', requestData);

          if (response.success) {
            this.pickupPhone.value = '';
            this.pickupStore.value = '';
            this.closePickupForm();
            this.validatePickupForm();
            this.showMessage(this.statusMessage, this.strings.pickupSuccess, 'success');
          } else {
            this.showMessage(this.pickupMessage, response.error_message || this.strings.genericError, 'error');
          }
        } catch (error) {
          console.error(error);
          this.showMessage(this.pickupMessage, this.strings.genericError, 'error');
        } finally {
          this.setLoading(this.pickupSubmit, false);
          this.validatePickupForm();
        }
      }

      async submitAvailabilityReminder() {
        this.hideMessage(this.reminderMessage);
        this.hideMessage(this.statusMessage);
        this.clearReminderErrors();

        const requestData = {
          action: 'record email',
          variant_id: this.dataset.variantId,
          location: this.reminderLocation.value.trim(),
          email: this.reminderEmail.value.trim(),
          phone: this.reminderPhone.value.trim(),
          postal_code: this.reminderPostalCode.value.trim(),
          signup_for_sms: this.reminderSmsConsent.checked ? '1' : '0',
          signup_for_email: this.reminderEmailConsent.checked ? '1' : '0',
          language: this.dataset.language,
        };

        const hasInvalidEmail = !this.isValidEmail(requestData.email);
        const hasInvalidPhone =
          requestData.phone !== '' && this.reminderPhone.classList.contains('border-danger');

        this.setFieldError(this.reminderEmail, hasInvalidEmail);

        if (hasInvalidEmail || hasInvalidPhone) {
          this.showMessage(this.reminderMessage, this.strings.validationError, 'error');
          return;
        }

        this.setLoading(this.reminderSubmit, true);

        try {
          const response = await this.postForm('https://app.stevesmusic.com/api/availability-reminders', requestData);

          if (response.success) {
            this.closeReminderModal();
            this.resetReminderForm();
            this.showMessage(this.statusMessage || this.reminderMessage, this.strings.reminderSuccess, 'success');
          } else {
            this.applyReminderFieldErrors(response.fields || []);
            this.showMessage(this.reminderMessage, response.message || this.strings.genericError, 'error');
          }
        } catch (error) {
          console.error(error);
          this.showMessage(this.reminderMessage, this.strings.genericError, 'error');
        } finally {
          this.setLoading(this.reminderSubmit, false);
        }
      }

      async postForm(url, data) {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
          },
          body: this.toUrlEncodedBody(data),
        });

        return response.json();
      }

      toUrlEncodedBody(data) {
        const params = new URLSearchParams();
        const append = (key, value) => {
          if (value === undefined || value === null) {
            params.append(key, '');
          } else if (Array.isArray(value)) {
            value.forEach((item, index) => {
              const nextKey = item && typeof item === 'object' ? `${key}[${index}]` : `${key}[]`;
              append(nextKey, item);
            });
          } else if (typeof value === 'object') {
            Object.keys(value).forEach((childKey) => {
              append(`${key}[${childKey}]`, value[childKey]);
            });
          } else {
            params.append(key, String(value));
          }
        };

        Object.keys(data).forEach((key) => append(key, data[key]));
        return params;
      }

      getQuantity() {
        const form = this.closest('form');
        const quantityInput =
          form?.querySelector('quantity-input input[name="quantity"]') ||
          form?.querySelector('input[name="quantity"]') ||
          this.closest('product-info')?.querySelector('quantity-input input[name="quantity"]');

        return quantityInput?.value?.trim() || '1';
      }

      setLoading(button, isLoading) {
        if (!button) return;

        button.disabled = isLoading;
        button.classList.toggle('btn--loading', isLoading);
      }

      showMessage(container, message, type) {
        if (!container) return;

        container.textContent = message;
        container.classList.toggle('alert--success', type === 'success');
        container.classList.toggle('alert--error', type === 'error');
        container.hidden = false;
      }

      hideMessage(container) {
        if (!container) return;

        container.textContent = '';
        container.classList.remove('alert--success', 'alert--error');
        container.hidden = true;
      }

      clearReminderErrors() {
        [
          this.reminderLocation,
          this.reminderEmail,
          this.reminderPhone,
          this.reminderPostalCode,
          this.reminderEmailConsent,
          this.reminderSmsConsent,
        ].forEach((field) => this.clearFieldError(field));
      }

      applyReminderFieldErrors(fields) {
        const fieldMap = {
          '#availability-reminder-location': this.reminderLocation,
          '#availability-reminder-email': this.reminderEmail,
          '#availability-reminder-phone': this.reminderPhone,
          '#availability-reminder-zip': this.reminderPostalCode,
          '#availability-reminder-phone-checkbox': this.reminderSmsConsent,
        };

        fields.forEach((selector) => this.setFieldError(fieldMap[selector], true));
      }

      resetReminderForm() {
        this.reminderPhone.value = '';
        this.reminderPostalCode.value = '';
        this.reminderEmailConsent.checked = false;
        this.reminderSmsConsent.checked = false;
        this.clearReminderErrors();
        this.hideMessage(this.reminderMessage);
      }
    }
  );
}